// src/utils/websocket/WebSocketManager.ts
import { ref, watchEffect } from "vue";
import { useWebSocket } from "@vueuse/core";
import { getAccessToken } from "@/utils/auth";
import { config } from "@/config/axios/config";

export interface WebSocketMessage {
  type: string;
  content: any;
  timestamp?: number;
}

export interface MessageHandler {
  type: string;
  handler: (content: any) => void;
}

class WebSocketManager {
  private static instance: WebSocketManager | null;
  private baseServerUrl: string;
  private messageHandlers: Map<string, ((content: any) => void)[]> = new Map();

  // VueUse WebSocket 相关
  private status = ref<"OPEN" | "CONNECTING" | "CLOSED">("CLOSED");
  private sendFn: ((data: string | ArrayBuffer | Blob) => void) | null = null;
  private closeFn: (() => void) | null = null;
  private openFn: (() => void) | null = null;
  private interval: ReturnType<typeof setInterval> | undefined;

  // 防止 initWebSocket 并发重入
  private initializing = false;

  // 当前连接使用的 token 快照，用于检测 token 变化（登录/续期/登出）
  private currentToken: string | null = null;
  private tokenWatchInterval: ReturnType<typeof setInterval> | undefined;

  constructor() {
    this.baseServerUrl = this.buildBaseServerUrl();
    this.initWebSocket();
    this.watchTokenChange();
  }

  public static getInstance(): WebSocketManager {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager();
    }
    return WebSocketManager.instance;
  }

  /**
   * 构建 WebSocket 基础 URL
   * 优先使用 VITE_BASE_URL，如果为空则使用当前域名
   */
  private buildBaseServerUrl(): string {
    const baseUrl = import.meta.env.VITE_BASE_URL || "";

    // 获取 WebSocket 协议
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";

    // 如果 baseUrl 为空或无效，使用当前域名
    if (!baseUrl || baseUrl === "/" || baseUrl.trim() === "") {
      // 使用当前浏览器域名 + /infra/ws 路径
      const host = window.location.host;
      return `${wsProtocol}//${host}/infra/ws`;
    }

    // 转换 baseUrl 为 WebSocket URL
    let wsUrl = baseUrl;

    // 如果已经包含协议头，直接替换 http/https 为 ws/wss
    if (wsUrl.startsWith("http://") || wsUrl.startsWith("https://")) {
      wsUrl = wsUrl.replace(/^https?/, "ws");
    } else {
      // 如果没有协议头，添加当前域名和 ws 协议
      const host = window.location.host;
      // 确保路径以 / 开头
      wsUrl = wsUrl.startsWith("/") ? wsUrl : `/${wsUrl}`;
      wsUrl = `${wsProtocol}//${host}${wsUrl}`;
    }

    // 确保路径以 /infra/ws 结尾
    // 移除末尾可能存在的斜杠
    wsUrl = wsUrl.replace(/\/+$/, "");

    // 如果路径不包含 /infra/ws，则添加
    if (!wsUrl.endsWith("/infra/ws")) {
      wsUrl = `${wsUrl}/infra/ws`;
    }

    return wsUrl;
  }

  /**
   * 轮询检测 token 变化（登录 / refresh token 续期 / 登出）。
   * token 存储在 localStorage 中，非响应式，无法用 watch 直接侦听，
   * 这里用低频轮询 + 快照对比的方式感知变化：
   * - token 由无 → 有：建立连接
   * - token 变成新值：关闭旧连接并用新 token 重建
   * - token 被清除：关闭连接
   */
  private watchTokenChange() {
    // 避免重复启动
    if (this.tokenWatchInterval) return;
    this.tokenWatchInterval = setInterval(() => {
      const latest = getAccessToken();
      if (latest === this.currentToken) return;

      // token 被清除（登出）
      if (latest == null) {
        if (this.currentToken != null) {
          console.log("WebSocket: 检测到 token 被清除，关闭连接");
          this.closeFn?.();
          this.currentToken = null;
        }
        return;
      }

      // token 发生变化（首次登录或续期），重建连接
      console.log("WebSocket: 检测到 token 变化，重建连接");
      this.currentToken = latest;
      this.closeFn?.();
      this.status.value = "CLOSED";
      this.initializing = false;
      this.initWebSocket();
    }, 3000);
  }

  private initWebSocket() {
    const token = getAccessToken();
    if (token == null) {
      // 未登录时静默跳过，交由登录流程在拿到 token 后再初始化
      return;
    }

    // 防止并发重入：上一次初始化尚未结束或连接仍存活时直接返回
    if (this.initializing || this.status.value !== "CLOSED") {
      return;
    }
    this.initializing = true;

    // 记录本次连接使用的 token 快照
    this.currentToken = token;

    // 清理上一次的连接，避免旧实例的 autoReconnect 计时器继续累积造成泄漏
    this.closeFn?.();

    const serverUrl = `${this.baseServerUrl}?token=${token}`;

    console.log("WebSocket 连接地址:", serverUrl);

    const { status, data, send, close, open } = useWebSocket(serverUrl, {
      autoReconnect: {
        retries: -1,
        delay: 3000,
        onFailed: () => {
          console.warn("WebSocket 重连失败，已停止自动重连");
        },
      },
      heartbeat: {
        interval: 30000,
        message: "ping",
      },
      onConnected: () => {
        this.status.value = "OPEN";
        this.initializing = false;
        console.log("WebSocket 链接成功");
      },
      onDisconnected: () => {
        this.status.value = "CLOSED";
        this.initializing = false;
        console.log("WebSocket 链接断开");
      },
      onError: (error) => {
        console.error("WebSocket 链接错误:", error);
      },
    });

    this.sendFn = send;
    this.closeFn = close;
    this.openFn = open;

    // 监听消息
    watchEffect(() => {
      if (!data.value) return;

      if (data.value === "pong") return;

      try {
        const jsonMessage: WebSocketMessage = JSON.parse(data.value);
        this.handleMessage(jsonMessage);
      } catch (error) {
        console.error("WebSocket message parse error:", error);
      }
    });

    // 监听状态变化
    watchEffect(() => {
      this.status.value = status.value;
    });

    this.initializing = false;
  }

  private handleMessage(message: WebSocketMessage) {
    const { type, content } = message;
    console.log("收到 WebSocket 消息:", message);

    // 调用注册的消息处理器
    const handlers = this.messageHandlers.get(type);
    if (handlers) {
      handlers.forEach((handler) => handler(content));
    }
  }

  // 公共方法
  public send(type: string, content: any = {}) {
    if (!this.isConnected) {
      useMessage().warning("WebSocket 未连接");
      return false;
    }

    try {
      const payload: WebSocketMessage = {
        type,
        content,
        timestamp: Date.now(),
      };
      this.sendFn?.(JSON.stringify(payload));
      return true;
    } catch (error) {
      console.error("发送消息错误:", error);
      useMessage().error("发送消息失败");
      return false;
    }
  }

  public onMessage(type: string, handler: (content: any) => void) {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    this.messageHandlers.get(type)!.push(handler);
  }

  public offMessage(type: string, handler?: (content: any) => void) {
    if (!this.messageHandlers.has(type)) return;

    if (handler) {
      const handlers = this.messageHandlers.get(type)!;
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    } else {
      this.messageHandlers.delete(type);
    }
  }

  public toggleConnection() {
    if (this.isConnected) {
      this.closeFn?.();
    } else {
      this.openFn?.();
    }
  }

  // 状态获取
  public get isConnected() {
    return this.status.value === "OPEN";
  }

  /**
   * 关闭 WebSocket 连接，无条件清理定时器与单例
   */
  public closeWebSocket() {
    this.closeFn?.();
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = undefined;
    }
    if (this.tokenWatchInterval) {
      clearInterval(this.tokenWatchInterval);
      this.tokenWatchInterval = undefined;
    }
    this.status.value = "CLOSED";
    this.initializing = false;
    this.currentToken = null;
    this.sendFn = null;
    this.closeFn = null;
    this.openFn = null;
    WebSocketManager.instance = null;
  }
}

export default WebSocketManager;
